import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mail, Phone, MessageCircle, CheckCircle, XCircle, Clock, ArrowRight, Settings, Loader2, Video, Users, Lock, Link as LinkIcon, Folder } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import RecallMeetingsSection from '../components/RecallMeetingsSection';
import LinkGroupToDossierDialog from '../components/LinkGroupToDossierDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusIndicator({ connected, status, lastSync }) {
  if (!connected) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
        <span className="text-sm text-muted-foreground">Non connecte</span>
      </div>
    );
  }
  const s = status || 'connected';
  if (s === 'completed' || s === 'connected') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Connecte</span>
        </div>
        {lastSync && <p className="text-xs text-muted-foreground">Derniere sync: {new Date(lastSync).toLocaleString('fr-FR')}</p>}
      </div>
    );
  }
  if (s === 'configured') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-amber-500" />
        <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Configure</span>
      </div>
    );
  }
  if (s === 'failed') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-destructive" />
        <span className="text-sm text-destructive">Erreur</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
      <span className="text-sm text-amber-600 dark:text-amber-400">Sync en cours</span>
    </div>
  );
}

export default function SourcesPage() {
  const { personId } = useParams();
  const { token, user, tenant } = useAuth();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [emailSource, setEmailSource] = useState(null);
  const [ringoverSource, setRingoverSource] = useState(null);
  const [aircallSource, setAircallSource] = useState(null);
  const [teamsSource, setTeamsSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waNumber, setWaNumber] = useState('');
  const [savingWa, setSavingWa] = useState(false);
  const [isWaDialogOpen, setIsWaDialogOpen] = useState(false);
  const [waConfirmOpen, setWaConfirmOpen] = useState(false);
  const [waSession, setWaSession] = useState(null);
  const [waConnecting, setWaConnecting] = useState(false);
  const [waPolling, setWaPolling] = useState(false);

  const [waGroups, setWaGroups] = useState([]);
  const [waGroupsLoading, setWaGroupsLoading] = useState(false);
  const [linkGroupTarget, setLinkGroupTarget] = useState(null);

  const canManage = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    fetchAll();
  }, [personId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [personRes, emailRes, ringoverRes, aircallRes, teamsRes, waGroupsRes] = await Promise.allSettled([
        axios.get(`${API}/persons/${personId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${personId}/sources/email/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${personId}/sources/ringover/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${personId}/sources/aircall/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${personId}/sources/teams/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons/${personId}/sources/whatsapp/groups?person_id=${personId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (personRes.status === 'fulfilled') {
        setPerson(personRes.value.data);
        setWaNumber(personRes.value.data.whatsapp_number || '');
      }
      if (emailRes.status === 'fulfilled') setEmailSource(emailRes.value.data);
      if (ringoverRes.status === 'fulfilled') setRingoverSource(ringoverRes.value.data);
      if (aircallRes.status === 'fulfilled') setAircallSource(aircallRes.value.data);
      if (teamsRes.status === 'fulfilled') setTeamsSource(teamsRes.value.data);
      if (waGroupsRes.status === 'fulfilled') setWaGroups(waGroupsRes.value.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    setSavingWa(true);
    try {
      await axios.patch(`${API}/persons/${personId}`, { whatsapp_number: waNumber || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Numero WhatsApp mis a jour');
      setIsWaDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setSavingWa(false);
    }
  };

  const handleConnectWhatsApp = async () => {
    // Phase: confirmation modal first to avoid wrong-clone mistakes
    setWaConfirmOpen(true);
  };

  const confirmAndConnectWhatsApp = async () => {
    setWaConfirmOpen(false);
    setWaConnecting(true);
    setIsWaDialogOpen(true);
    try {
      await axios.post(`${API}/persons/${personId}/sources/whatsapp/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Start polling for QR/status
      setWaPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de connexion WhatsApp');
      setWaConnecting(false);
    }
  };

  const pollWhatsAppSession = async () => {
    try {
      const res = await axios.get(`${API}/persons/${personId}/sources/whatsapp/session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaSession(prev => {
        // Only show toast when transitioning TO connected (not on every poll)
        if (res.data.status === 'connected' && prev?.status !== 'connected') {
          toast.success('WhatsApp connecte !');
          fetchAll();
        }
        return res.data;
      });
      if (res.data.status === 'connected') {
        setWaPolling(false);
        setWaConnecting(false);
      }
      return res.data;
    } catch (e) {
      return null;
    }
  };

  const handleDisconnectWhatsApp = async () => {
    try {
      await axios.delete(`${API}/persons/${personId}/sources/whatsapp/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaSession(null);
      setWaPolling(false);
      toast.success('WhatsApp deconnecte');
      fetchAll();
    } catch (e) {
      toast.error('Erreur de deconnexion');
    }
  };

  const handleToggleGroup = async (groupId) => {
    try {
      const res = await axios.put(
        `${API}/persons/${personId}/sources/whatsapp/groups/${encodeURIComponent(groupId)}/toggle?person_id=${personId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWaGroups(prev => prev.map(g =>
        g.group_id === groupId ? { ...g, excluded: res.data.excluded } : g
      ));
      toast.success(res.data.excluded ? 'Groupe exclu de la synchronisation' : 'Groupe inclus dans la synchronisation');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur';
      toast.error(msg);
    }
  };

  // Poll for QR code / connection status
  useEffect(() => {
    if (!waPolling) return;
    const interval = setInterval(pollWhatsAppSession, 2000);
    pollWhatsAppSession();
    return () => clearInterval(interval);
  }, [waPolling]);

  // Check session on load (silent — no toast)
  useEffect(() => {
    if (person?.whatsapp_number) {
      const checkSession = async () => {
        try {
          const res = await axios.get(`${API}/persons/${personId}/sources/whatsapp/session`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setWaSession(res.data);
        } catch (e) { /* ignore */ }
      };
      checkSession();
    }
  }, [person]);

  if (loading) {
    return (
      <DashboardLayout title="Sources">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Sources">
      <div className="max-w-3xl space-y-6" data-testid="sources-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clones')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">{person?.name}</h2>
            <p className="text-sm text-muted-foreground">Gestion des sources de donnees</p>
          </div>
        </div>

        {/* Email Source */}
        <Card data-testid="email-source-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10"><Mail className="w-5 h-5 text-blue-500" /></div>
                <div>
                  <CardTitle className="text-base font-heading">Email (IMAP)</CardTitle>
                  <CardDescription className="text-xs">Ingestion automatique des emails</CardDescription>
                </div>
              </div>
              <StatusIndicator connected={!!emailSource} status={emailSource?.last_sync_status} lastSync={emailSource?.last_sync_at} />
            </div>
          </CardHeader>
          <CardContent>
            {emailSource ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm">{emailSource.email_address || 'Email configure'}</p>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs">{emailSource.emails_synced_count || 0} emails syncs</Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/clones/${personId}/email`)} data-testid="manage-email-btn">
                  Gerer <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Aucune source email connectee</p>
                {canManage && (
                  <Button size="sm" onClick={() => navigate(`/clones/${personId}/email`)} data-testid="connect-email-btn">
                    Connecter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ringover Source */}
        <Card data-testid="ringover-source-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10"><Phone className="w-5 h-5 text-orange-500" /></div>
                <div>
                  <CardTitle className="text-base font-heading">Ringover</CardTitle>
                  <CardDescription className="text-xs">Appels et SMS</CardDescription>
                </div>
              </div>
              <StatusIndicator connected={!!ringoverSource} status={ringoverSource?.last_sync_status} lastSync={ringoverSource?.last_sync_at} />
            </div>
          </CardHeader>
          <CardContent>
            {ringoverSource ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs">{ringoverSource.calls_synced_count || 0} appels</Badge>
                    <Badge variant="outline" className="text-xs">{ringoverSource.sms_synced_count || 0} SMS</Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/clones/${personId}/ringover`)} data-testid="manage-ringover-btn">
                  Gerer <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Aucune source Ringover connectee</p>
                {canManage && (
                  <Button size="sm" onClick={() => navigate(`/clones/${personId}/ringover`)} data-testid="connect-ringover-btn">
                    Connecter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aircall Source */}
        <Card data-testid="aircall-source-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-500/10"><Phone className="w-5 h-5 text-sky-500" /></div>
                <div>
                  <CardTitle className="text-base font-heading">Aircall</CardTitle>
                  <CardDescription className="text-xs">Appels (IVR natif)</CardDescription>
                </div>
              </div>
              <StatusIndicator connected={!!aircallSource} status={aircallSource?.last_sync_status} lastSync={aircallSource?.last_sync_at} />
            </div>
          </CardHeader>
          <CardContent>
            {aircallSource ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs">{aircallSource.calls_synced_count || 0} appels</Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/clones/${personId}/aircall`)} data-testid="manage-aircall-btn">
                  Gerer <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Aucune source Aircall connectee</p>
                {canManage && (
                  <Button size="sm" onClick={() => navigate(`/clones/${personId}/aircall`)} data-testid="connect-aircall-btn">
                    Connecter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Microsoft Teams Source */}
        <Card data-testid="teams-source-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/10"><Video className="w-5 h-5 text-indigo-500" /></div>
                <div>
                  <CardTitle className="text-base font-heading">Microsoft Teams</CardTitle>
                  <CardDescription className="text-xs">Appels PSTN + reunions</CardDescription>
                </div>
              </div>
              <StatusIndicator connected={!!teamsSource} status={teamsSource?.last_sync_status} lastSync={teamsSource?.last_sync_at} />
            </div>
          </CardHeader>
          <CardContent>
            {teamsSource ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs">{teamsSource.calls_synced_count || 0} appels</Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/clones/${personId}/teams`)} data-testid="manage-teams-btn">
                  Gerer <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Aucune source Teams connectee</p>
                {canManage && (
                  <Button size="sm" onClick={() => navigate(`/clones/${personId}/teams`)} data-testid="connect-teams-btn">
                    Connecter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recall.ai Meetings (universal bot Teams/Zoom/Meet) */}
        <RecallMeetingsSection personId={personId} token={token} canManage={canManage} />

        {/* WhatsApp Source — visible only if feature enabled for tenant */}
        {tenant?.features?.whatsapp && (<>
        <Card data-testid="whatsapp-source-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10"><MessageCircle className="w-5 h-5 text-green-500" /></div>
                <div>
                  <CardTitle className="text-base font-heading">WhatsApp</CardTitle>
                  <CardDescription className="text-xs">Connexion via QR code</CardDescription>
                </div>
              </div>
              {waSession?.status === 'connected' ? (
                <StatusIndicator connected={true} status="connected" />
              ) : person?.whatsapp_number ? (
                <StatusIndicator connected={true} status="configured" />
              ) : (
                <StatusIndicator connected={false} />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {waSession?.status === 'connected' ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-mono">{waSession.owner_number || person?.whatsapp_number}</p>
                  <p className="text-xs text-muted-foreground">Session WhatsApp active — messages ingeres automatiquement</p>
                </div>
                {canManage && (
                  <Button variant="outline" size="sm" onClick={handleDisconnectWhatsApp} className="text-destructive" data-testid="disconnect-whatsapp-btn">
                    Deconnecter
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {person?.whatsapp_number ? `Numero configure: ${person.whatsapp_number} — non connecte` : 'Scannez un QR code pour connecter WhatsApp'}
                </p>
                {canManage && (
                  <Button size="sm" onClick={handleConnectWhatsApp} disabled={waConnecting} data-testid="connect-whatsapp-btn">
                    {waConnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Connecter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* WhatsApp Groups Management */}
        {waGroups.length > 0 && (
          <Card data-testid="whatsapp-groups-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10"><Users className="w-5 h-5 text-green-500" /></div>
                  <div>
                    <CardTitle className="text-base font-heading">Groupes WhatsApp</CardTitle>
                    <CardDescription className="text-xs">{waGroups.length} groupe{waGroups.length > 1 ? 's' : ''} detecte{waGroups.length > 1 ? 's' : ''} — activez ou desactivez la synchronisation</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {waGroups.map(g => (
                  <div key={g.group_id} className="flex items-center justify-between py-2.5 px-2 rounded-md hover:bg-muted/50 transition-colors" data-testid={`wa-group-row-${g.group_id}`}>
                    <div className="space-y-0.5 min-w-0 flex-1 mr-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{g.group_name || 'Groupe inconnu'}</p>
                        {g.excluded_by_admin && (
                          <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
                            <Lock className="w-2.5 h-2.5" /> Admin
                          </Badge>
                        )}
                        {g.dossier_id && (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 shrink-0 border-indigo-300 text-indigo-700 dark:text-indigo-300 dark:border-indigo-800"
                            data-testid={`wa-group-linked-${g.group_id}`}
                          >
                            <Folder className="w-2.5 h-2.5" />
                            {g.dossier_title || 'Dossier lié'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{g.participant_count || 0} participants</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-xs"
                        onClick={() => setLinkGroupTarget(g)}
                        data-testid={`wa-group-link-btn-${g.group_id}`}
                        title={g.dossier_id ? 'Modifier le lien' : 'Lier à un dossier'}
                      >
                        <LinkIcon className="w-3.5 h-3.5 mr-1" />
                        {g.dossier_id ? 'Modifier' : 'Lier'}
                      </Button>
                      <Switch
                        checked={!g.excluded}
                        disabled={g.excluded_by_admin && !canManage}
                        onCheckedChange={() => handleToggleGroup(g.group_id)}
                        data-testid={`wa-group-toggle-${g.group_id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* WhatsApp Connection Confirmation Dialog */}
        <AlertDialog open={waConfirmOpen} onOpenChange={setWaConfirmOpen}>
          <AlertDialogContent data-testid="whatsapp-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                Confirmer la connexion WhatsApp
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 pt-2">
                  <div className="text-sm">
                    Tu vas connecter un compte WhatsApp au clone :
                  </div>
                  <div className="bg-muted/60 border rounded-lg p-4 text-center">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Clone cible</div>
                    <div className="font-heading font-bold text-xl tracking-tight">
                      {person?.name || `${person?.first_name || ''} ${person?.last_name || ''}`.trim() || 'Sans nom'}
                    </div>
                    {tenant?.name && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Tenant : <span className="font-medium">{tenant.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-3">
                    ⚠️ Tous les messages WhatsApp futurs de ce compte seront ingérés
                    dans le RAG de ce clone. Vérifie bien que c'est la bonne fiche.
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="whatsapp-confirm-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAndConnectWhatsApp}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="whatsapp-confirm-ok"
              >
                Oui, connecter à {person?.name?.split(' ')[0] || 'ce clone'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* WhatsApp QR Dialog */}
        <Dialog open={isWaDialogOpen} onOpenChange={(open) => { if (!open) { setWaPolling(false); setWaConnecting(false); } setIsWaDialogOpen(open); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Connecter WhatsApp</DialogTitle>
              <DialogDescription>Scannez le QR code avec WhatsApp sur votre telephone</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-6 space-y-4">
              {waSession?.status === 'connected' ? (
                <div className="text-center space-y-2">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
                  <p className="font-medium text-emerald-600">WhatsApp connecte !</p>
                  <p className="text-sm text-muted-foreground font-mono">{waSession.owner_number}</p>
                </div>
              ) : waSession?.qr_data_url ? (
                <div className="text-center space-y-3">
                  <img src={waSession.qr_data_url} alt="QR Code WhatsApp" className="w-64 h-64 mx-auto rounded-lg border" data-testid="whatsapp-qr-code" />
                  <p className="text-sm text-muted-foreground">Ouvrez WhatsApp &gt; Appareils connectes &gt; Scanner</p>
                </div>
              ) : (
                <div className="text-center space-y-3">
                  <Loader2 className="w-12 h-12 animate-spin text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    {waSession?.status === 'initializing' ? 'Initialisation de la session...' :
                     waSession?.status === 'reconnecting' ? 'Reconnexion en cours...' :
                     'Generation du QR code...'}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsWaDialogOpen(false); setWaPolling(false); setWaConnecting(false); }}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link group to dossier dialog */}
        <LinkGroupToDossierDialog
          open={!!linkGroupTarget}
          onOpenChange={(o) => { if (!o) setLinkGroupTarget(null); }}
          personId={personId}
          group={linkGroupTarget}
          onSaved={({ dossier_id, dossier_title }) => {
            setWaGroups(prev => prev.map(g =>
              g.group_id === linkGroupTarget?.group_id
                ? { ...g, dossier_id, dossier_title }
                : g
            ));
          }}
        />
        </>)}
      </div>
    </DashboardLayout>
  );
}
